import { create } from 'zustand';
import { emptyManifest, workspaceRequest, type WorkspaceManifest, type WorkspaceResourceType } from './workspaceApi';

interface State {
  identity: string;
  projectId: string | null;
  manifest: WorkspaceManifest;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string;
  initialize: (identity: string) => Promise<void>;
  refresh: () => Promise<void>;
  openProject: (id: string | null) => void;
  place: (type: WorkspaceResourceType, id: string, projectId: string | null, folderId?: string | null) => Promise<void>;
  reset: () => void;
}

function storedProject(identity: string): string | null {
  try { return sessionStorage.getItem(`nexus-workspace:${identity}`); } catch { return null; }
}

let generation = 0;
export const useWorkspaceStore = create<State>((set, get) => ({
  identity: '', projectId: null, manifest: emptyManifest(), status: 'idle', error: '',
  initialize: async identity => {
    if (get().identity === identity && get().status !== 'idle') return;
    const request = ++generation;
    set({ identity, projectId: storedProject(identity), manifest: emptyManifest(), status: 'loading', error: '' });
    try {
      const manifest = await workspaceRequest<WorkspaceManifest>();
      if (request !== generation) return;
      const selected = get().projectId;
      set({ manifest, status: 'ready', projectId: manifest.projects.some(p => p.id === selected) ? selected : null });
    } catch (error) {
      if (request === generation) set({ status: 'error', error: (error as Error).message });
    }
  },
  refresh: async () => {
    const request = ++generation;
    const manifest = await workspaceRequest<WorkspaceManifest>();
    if (request !== generation) return;
    const projectId = get().projectId;
    set({ manifest, status: 'ready', error: '', projectId: manifest.projects.some(p => p.id === projectId) ? projectId : null });
  },
  openProject: projectId => {
    if (projectId && !get().manifest.projects.some(p => p.id === projectId)) return;
    set({ projectId, error: '' });
    try {
      const key = `nexus-workspace:${get().identity}`;
      if (projectId) sessionStorage.setItem(key, projectId); else sessionStorage.removeItem(key);
    } catch { /* A blocked session store does not prevent navigation. */ }
  },
  place: async (resourceType, resourceId, projectId, folderId = null) => {
    await workspaceRequest('/placement', 'PUT', { resourceType, resourceId, projectId, folderId });
    await get().refresh();
  },
  reset: () => { generation++; set({ identity: '', projectId: null, manifest: emptyManifest(), status: 'idle', error: '' }); },
}));

/** UI organization filter; each resource service still enforces authorization. */
export function resourceInProject(type: WorkspaceResourceType, id: string, projectId = useWorkspaceStore.getState().projectId): boolean {
  if (!projectId) return true;
  return useWorkspaceStore.getState().manifest.placements.some(p => p.resourceType === type && p.resourceId === id && p.projectId === projectId);
}

/** A resource the server creates as a side effect of another one (a notebook
 *  exporting to a new object type) never goes through a `POST /<collection>`,
 *  so workspaceFetch cannot place it. Put it where its origin lives (same
 *  project and folder), or in the open project when the origin is unplaced.
 *  Never throws: the resource exists either way. */
export async function placeBesideOrigin(
  type: WorkspaceResourceType, id: string, originType: WorkspaceResourceType, originId?: string | null,
): Promise<void> {
  const st = useWorkspaceStore.getState();
  const origin = originId
    ? st.manifest.placements.find(p => p.resourceType === originType && p.resourceId === originId)
    : undefined;
  const projectId = origin?.projectId ?? st.projectId;
  if (!projectId) return;
  try {
    await st.place(type, id, projectId, origin?.projectId ? origin.folderId ?? null : null);
  } catch {
    useWorkspaceStore.setState({ error: 'El recurso se creó, pero no se pudo agregar al proyecto. Puedes organizarlo desde Archivos → Todos los proyectos.' });
  }
}
